import React, { FunctionComponent, useState } from 'react';
import withStyles from 'react-jss';
import queryString from 'query-string';
import {
  map,
  values,
  mergeDeepRight,
  merge,
  pickAll,
  omit,
  isEmpty,
  flatten,
} from 'ramda';
import { useLocation } from 'react-router-dom';
import { Redirect } from 'react-router';
import { Container } from 'semantic-ui-react';

import GoogleAPI from 'helpers/googleAPIHandler';
import { ClassProp } from 'helpers/types';
import { DefaultError } from 'scenes/Report/components/Query/Query.helpers';
import { ReportParams } from 'scenes/Report/components/ReportBase/components/ParamsHeader';
import { CSVPlaceholder } from 'components/Placeholder/presets';
import { BatchData } from 'components/DownloadWidget/DownloadWidget';

import GenerationResults from './scenes/GenerationResults';
import {
  reportsConfig,
  reportReference,
  BatchConfigSegmentedData,
} from './BatchReport.config';
import { useGetReportsData } from '../AsyncReport/AsyncReport.useGetReportsData.hook';
import {
  uploadImages,
  getTemplateURL,
  allReportsHaveImages,
  slideBatchUpdate,
} from './BatchReport.helpers';
import { ReportProps } from './scenes/GenerationResults/GenerationResults';

const styles = {
  reportContainer: {
    opacity: '0 !important',
    position: 'fixed !important',

    '& *': {
      textAlign: 'center !important',
    },
    '& .rv-xy-plot__axis__tick__text': {
      fontSize: '1rem !important',
    },
    '& .rv-discrete-color-legend *': {
      fontSize: '1.25rem !important',
    },
  },
};

type Styles = ClassProp<typeof styles>;

export type ValidReports = {
  [key: string]: Record<string, unknown>;
};

type SegmentedReports = {
  [key: string]: any;
};

type FinalReports = {
  validReports: ValidReports;
  invalidReports: Record<string, unknown>[];
};

type Props = {
  classes: Styles;
};

const googleAPI = new GoogleAPI();

export const BatchReportContext = React.createContext<Function | null>(null);
export const ReportDataContext = React.createContext<Record<
  string,
  unknown
> | null>(null);

const noValidReportsError =
  'There were unable to generate any reports at this time';

const renderRedirect = (error: string) => {
  const state = [
    {
      displayMessage: 'Something went wrong',
      message: error,
    },
  ];

  return (
    <Redirect
      to={{
        state,
        pathname: '/reports',
      }}
    />
  );
};

const renderReports = map((reportName: string) => {
  const report = reportReference[reportName];
  if (report) {
    const { Component, props } = report;

    return <Component {...props} key={reportName} />;
  }

  return renderRedirect(`You are trying to access an invalid report.`);
});

const renderGenerationProcess = (
  reportsList: string[],
  listener: (
    imageURL: string,
    imageBlob: Blob,
    reportName: string,
    error?: DefaultError | undefined,
    data?: BatchData | undefined,
    segmentedBatchData?: BatchConfigSegmentedData | undefined,
  ) => void,
  classes: Styles,
  reportsData: Record<string, unknown> | null,
) => (
  <BatchReportContext.Provider value={listener}>
    <ReportDataContext.Provider value={reportsData}>
      <CSVPlaceholder message="We're generating the reports" />

      <Container className={classes.reportContainer}>
        {renderReports(reportsList)}
      </Container>
    </ReportDataContext.Provider>
  </BatchReportContext.Provider>
);

const BatchReport: FunctionComponent<Props> = ({ classes }) => {
  const location = useLocation();
  const params = queryString.parse(location.search) as ReportParams;

  const [error, setError] = useState<string | null>(null);
  const [templateURL, setTemplateURL] = useState<string>('');
  const [finalReports, setFinalReports] = useState<FinalReports | null>(null);

  const onCompleted = (validReportList: string[]) => {
    if (isEmpty(validReportList)) {
      setError(noValidReportsError);
    }
  };

  const { reports, ...getReportsData } = useGetReportsData(params, onCompleted);

  let validReports: ValidReports =
    reports.validReports || pickAll(params.reportList || [], reportsConfig);

  let invalidReports: Record<string, unknown>[] =
    reports.invalidReports || values(omit(params.reportList, reportsConfig));

  const removeSegmentedBase: Record<string, string>[] = [];

  const segmentedReports: SegmentedReports = [];

  const addImagesToGoogleSlide = async (templateId: string) => {
    const imagesId = await uploadImages(validReports);

    await slideBatchUpdate(
      mergeDeepRight(imagesId, flatten(values(validReports))),
      invalidReports as ReportProps[],
      templateId,
      params,
      googleAPI.slideBatchUpdate,
      removeSegmentedBase,
    );

    return imagesId;
  };

  const initGoogleAPIConnection = async () => {
    try {
      await googleAPI.initClient();

      const templateName = `${params.merchantName} - Core Charts`;
      const { templateId }: any = await googleAPI.copyFile(templateName);

      await addImagesToGoogleSlide(templateId as string);

      setTemplateURL(getTemplateURL(templateId));
    } catch (err) {
      setError(err.displayMessage);
    } finally {
      setFinalReports({ validReports, invalidReports });
    }
  };

  const imageGeneratedListener = (
    imageURL: string,
    imageBlob: Blob,
    reportName: string,
    error?: DefaultError,
    data?: BatchData,
    segmentedBatchData?: BatchConfigSegmentedData,
  ) => {
    const report = merge(reportsConfig[reportName], {
      imageURL,
      imageBlob,
      error,
      data: segmentedBatchData || data,
    });

    if (segmentedBatchData) {
      const { isSegmented, segmentValue } = segmentedBatchData;
      if (isSegmented) {
        const value = segmentValue ? segmentValue : 'combined';
        report.imageName = `${report.imageName}_${value}`;
        (
          segmentedReports[reportName] || (segmentedReports[reportName] = [])
        ).push(report);
        removeSegmentedBase.push({
          name: report.name,
          pageId: report.pageId,
        });
      }
    }

    const hasValidReport = validReports[reportName];
    const invalidReport = error || isEmpty(imageURL);

    if (invalidReport && hasValidReport) {
      invalidReports = [...invalidReports, report];
      validReports = omit([reportName], validReports);
    } else if (hasValidReport) {
      validReports = { ...validReports, [reportName]: report };
      if (!isEmpty(segmentedReports)) {
        validReports = { ...validReports, ...segmentedReports };
      }
    }

    if (isEmpty(validReports)) {
      setError(noValidReportsError);
    }

    if (allReportsHaveImages(validReports) && !isEmpty(validReports)) {
      initGoogleAPIConnection();
    }
  };

  if (error) {
    return renderRedirect(error);
  }

  if (getReportsData.loading) {
    return <CSVPlaceholder message="We're generating the reports" />;
  }

  if ((getReportsData.reportsData && !finalReports) || !finalReports) {
    const { reportsData } = getReportsData;
    return renderGenerationProcess(
      params.reportList,
      imageGeneratedListener,
      classes,
      reportsData,
    );
  }

  return (
    <GenerationResults
      params={params}
      templateURL={templateURL}
      validReports={finalReports.validReports}
      invalidReports={finalReports.invalidReports}
    />
  );
};

export default withStyles(styles)(BatchReport);
